// Automation Module Tour
// Workflows, triggers, and automated sequences

export const automationTour = {
  id: 'automation',
  name: 'Automation Module Tour',
  displayName: 'Automate Your Business',
  description: 'Set up workflows that run 24/7 while you sleep',
  estimatedDuration: '8 minutes',
  category: 'feature',
  autoStart: false,
  prerequisiteTours: ['ftux', 'contacts'],
  
  config: {
    animate: true,
    overlayOpacity: 0.75,
    smoothScroll: true,
    allowClose: true,
    showProgress: true
  },
  
  steps: [
    // Step 1: Welcome to Automation
    {
      element: 'nav #sb_automation',
      popover: {
        title: '⚡ Welcome to Automation',
        description: `
          This is where the magic happens! Automation lets you build workflows that 
          run automatically—follow-up sequences, appointment reminders, lead nurturing, 
          and more. Set it once, and it runs forever. Your business works while you sleep!
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 2: What is a Workflow?
    {
      popover: {
        title: '🔄 What\'s a Workflow?',
        description: `
          A workflow is a series of automated actions triggered by an event:
          
          **Example**: "When someone fills out contact form..."
          → Wait 5 minutes
          → Send welcome email
          → Wait 2 days
          → Send follow-up SMS
          → Add to Hot Leads list
          
          The workflow runs automatically for every person who triggers it. 
          You build it once, it runs thousands of times!
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 3: Triggers vs Actions vs Conditions
    {
      popover: {
        title: '🎯 Building Blocks: Triggers, Actions, Conditions',
        description: `
          Every workflow has three key components:
          
          **TRIGGER**: What starts the workflow? (form submitted, tag added, etc.)
          
          **ACTIONS**: What should happen? (send email, add tag, create task)
          
          **CONDITIONS**: Should this action run? (if tag is X, if value > $1000)
          
          Think of it like: "WHEN [trigger], IF [condition], THEN DO [action]"
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 4: Workflow List
    {
      element: '[class*="workflow-list"], [class*="automation-list"]',
      popover: {
        title: '📋 Your Workflows',
        description: `
          This list shows all your workflows:
          • **Active**: Currently running
          • **Inactive**: Paused/turned off
          • **Draft**: Not published yet
          
          You'll see how many contacts are in each workflow, completion rates, 
          and performance stats. Click any workflow to edit it.
        `,
        side: 'left',
        align: 'start'
      }
    },

    // Step 5: Create New Workflow
    {
      element: 'button:contains("Create Workflow"), button:contains("New Workflow")',
      popover: {
        title: '➕ Create Your First Workflow',
        description: `
          Two ways to start:
          • **Recipe Library**: Pre-built workflows for common scenarios
          • **Start from Scratch**: Build completely custom workflow
          
          Recipes are fastest for beginners! Pick one that matches your goal 
          (e.g., "Follow up with form submissions"), customize it, and publish.
        `,
        side: 'left',
        align: 'start',
        proTip: 'Start with recipes! They\'re proven to work and teach you workflow best practices.'
      }
    },

    // Step 6: Recipe Library
    {
      element: '[href*="recipe"], button:contains("Recipe")',
      popover: {
        title: '📚 Recipe Library',
        description: `
          Dozens of pre-built workflows ready to use:
          • **Welcome Series**: Auto-nurture new leads
          • **Appointment Reminders**: Reduce no-shows
          • **Abandoned Cart**: Recover lost sales
          • **Re-engagement**: Win back inactive contacts
          • **Birthday Messages**: Personal touch at scale
          
          Just select a recipe, customize the messages, and activate!
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 7: Workflow Builder Interface
    {
      popover: {
        title: '🛠️ The Workflow Builder',
        description: `
          The builder is a visual canvas where you drag and connect elements:
          • **Left sidebar**: Available triggers, actions, conditions
          • **Canvas (center)**: Where you build the workflow
          • **Right panel**: Configure each element's settings
          
          It's like a flowchart that actually runs! Drag, connect, configure, publish.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 8: Common Triggers
    {
      popover: {
        title: '🎬 Popular Triggers',
        description: `
          Workflows start with triggers. Common ones:
          
          • **Form submitted**: Someone fills out a form
          • **Tag added**: Contact gets a specific tag
          • **Opportunity stage changed**: Deal moves in pipeline
          • **Appointment booked**: Calendar booking created
          • **Date/Time**: Specific date or time interval
          • **Email opened/clicked**: Engagement detected
          • **Contact created**: New person added
          
          Pick the right trigger for your goal!
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 9: Common Actions
    {
      popover: {
        title: '🎬 Popular Actions',
        description: `
          Once triggered, workflows perform actions:
          
          • **Send Email**: Automated email to contact
          • **Send SMS**: Text message
          • **Add/Remove Tag**: Organize contacts
          • **Create Task**: Remind yourself to do something
          • **Add to Opportunity**: Create a deal in pipeline
          • **Wait**: Delay before next action (1 hour, 2 days, etc.)
          • **If/Else**: Branch based on conditions
          • **Webhook**: Send data to external systems
          
          Chain actions together for complex sequences!
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 10: Wait Steps & Delays
    {
      popover: {
        title: '⏱️ Timing is Everything',
        description: `
          Use "Wait" steps to space out your communication:
          • Wait 1 hour → Send welcome email
          • Wait 3 days → Send follow-up
          • Wait until next Monday 9am → Send weekly tip
          
          **Don't overwhelm contacts!** Give them time to respond before the next message. 
          A well-timed sequence feels personal, not spammy.
        `,
        side: 'over',
        align: 'center',
        proTip: 'Test your timing! If people unsubscribe, you might be messaging too frequently.'
      }
    },

    // Step 11: Conditions & Branching
    {
      popover: {
        title: '🔀 Smart Branching with Conditions',
        description: `
          Make workflows smart with If/Else conditions:
          
          **Example**: "Did they open the email?"
          → YES: Wait 1 day, send next email
          → NO: Wait 2 days, send different email
          
          **Example 2**: "Is deal value > $5,000?"
          → YES: Notify sales manager
          → NO: Continue normal process
          
          Conditions let you personalize paths based on behavior or data.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 12: Testing Workflows
    {
      popover: {
        title: '🧪 Test Before You Launch',
        description: `
          ALWAYS test workflows before going live:
          1. Add yourself as a test contact
          2. Trigger the workflow (submit form, add tag, etc.)
          3. Verify emails arrive, timing is right, links work
          4. Check that tags and tasks are created correctly
          
          Catch mistakes in testing, not with real customers! Use "test@yourcompany.com" 
          or create a "Test Contacts" tag to easily identify test data.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 13: Workflow Analytics
    {
      element: '[href*="analytic"], [href*="reporting"]',
      popover: {
        title: '📊 Track Workflow Performance',
        description: `
          See how your automations perform:
          • **Contacts in workflow**: How many are currently going through it
          • **Completed**: How many finished the entire sequence
          • **Open rates**: For email actions
          • **Click rates**: For links in emails
          • **Conversion**: Did they take the desired action?
          
          Use this data to optimize. If completion is low, maybe it's too long!
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 14: Common Workflow Patterns
    {
      popover: {
        title: '💡 Proven Workflow Ideas',
        description: `
          Here are workflows every business should have:
          
          **1. Welcome Series**: New lead → 5 emails over 2 weeks introducing your business
          **2. Appointment Reminders**: Booked → reminder 1 day before + 1 hour before
          **3. Re-engagement**: No activity in 90 days → "We miss you" email
          **4. Lead Nurture**: Form submission → education sequence until they buy
          **5. Onboarding**: Customer created → welcome, setup guide, check-in calls
          **6. Win-back**: Lost opportunity → wait 60 days → "Still interested?" message
          
          Start with these, then get creative!
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 15: Best Practices
    {
      popover: {
        title: '✅ Automation Best Practices',
        description: `
          **DO:**
          • Start simple—one trigger, 2-3 actions
          • Test with yourself first
          • Give clear unsubscribe options
          • Monitor performance and optimize
          • Use Wait steps to pace communication
          
          **DON'T:**
          • Send too many messages too fast
          • Skip testing
          • Create overly complex workflows (hard to debug)
          • Forget to update when your business changes
          • Ignore unsubscribe requests
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 16: Stopping Workflows
    {
      popover: {
        title: '🛑 Stopping or Removing Contacts',
        description: `
          Need to stop a workflow for someone?
          • **Remove from workflow**: Stops them mid-sequence
          • **Pause workflow**: Stops everyone (for editing/testing)
          • **Add stop trigger**: Tag someone with "Stop Emails" to exit all workflows
          
          People can also opt out via unsubscribe links. Honor opt-outs immediately!
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 17: Completion
    {
      popover: {
        title: '🎉 You\'re an Automation Expert!',
        description: `
          Incredible! You now know how to:
          ✅ Build workflows with triggers, actions, and conditions
          ✅ Use the Recipe Library for quick starts
          ✅ Add wait steps for proper timing
          ✅ Branch with if/else logic
          ✅ Test workflows before launching
          ✅ Track and optimize performance
          
          **Next Steps:**
          • Build your first workflow (start with Welcome Series recipe!)
          • Test it with yourself as the contact
          • Create appointment reminder workflows
          • Set up a re-engagement workflow for cold leads
          
          Your business now runs on autopilot! 🤖✨
        `,
        side: 'over',
        align: 'center',
        showButtons: ['close'],
        doneBtnText: 'Automate Everything! →'
      }
    }
  ]
};
